/**
 * Sprint 12+M5.17-q (22.07.2026) — Cockpit-Onboarding-Tour fuer Erst-User.
 *
 * Ziel:
 *   Neue Pioniere sollen beim ersten Cockpit-Besuch in ~30 Sekunden
 *   verstehen, was sie im Cockpit erwartet. Danach nie wieder.
 *
 * UX-Prinzipien:
 *   - 3 Steps, klar getrennt, jeder mit Headline + 2-3 Zeilen Body
 *   - Fullscreen-Overlay auf Mobile (60% Traffic), zentrierte Card auf Desktop
 *   - Progress-Dots + Step-Zaehler "1/3", "2/3", "3/3"
 *   - Skip-Button in jedem Step (dismissible)
 *   - Vor-/Zurueck-Navigation
 *   - Body-Scroll wird waehrend Tour blockiert
 *
 * State:
 *   `localStorage.f4s_cockpit_tour_completed`
 *     "1" -> Tour wurde beendet oder gesynchronisiert -> Tour NICHT mehr zeigen
 *     null / anderer Wert -> Tour beim naechsten Mount zeigen
 *
 * Analytics (via window.dataLayer.push):
 *   { event: "cockpit_tour_started" }               beim ersten Render
 *   { event: "cockpit_tour_completed" }             wenn User "Los geht's" klickt
 *   { event: "cockpit_tour_skipped_at_step", step: 1|2|3 }
 *     wenn User Skip/Close klickt (Step ist die aktuelle 1-indexed Step-Nummer)
 *
 * Analytics wird zusaetzlich `typeof window.fbq === 'function'` respektiert —
 * bei fehlender Marketing-Zustimmung (Cookie-Banner Necessary-Only) laufen
 * die dataLayer-Events zwar, aber Pixel-Events werden geskippt.
 */
import React, { useEffect, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Compass, Users, LayoutDashboard, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = "f4s_cockpit_tour_completed";

function readCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCompleted() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* localStorage disabled -- Tour wuerde bei jedem Reload wieder erscheinen */
  }
}

function pushAnalytics(event, extra = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...extra });
  } catch {
    /* Analytics-Fehler duerfen die Tour niemals blockieren */
  }
}

const STEPS = [
  {
    id: 1,
    icon: Compass,
    accent: "#4563BB",
    accentBg: "#EEF2FF",
    kicker: "Schritt 1 von 3",
    headline: "Dein Profil",
    body:
      "Hier landet dein Quick-Check-Ergebnis. Du siehst deinen Fokustyp — " +
      "eule, biene, katze oder hund — und was er über dein Reaktionsmuster verrät.",
    highlight: "pionier-quickcheck-section",
  },
  {
    id: 2,
    icon: Users,
    accent: "#7C3AED",
    accentBg: "#F3E8FF",
    kicker: "Schritt 2 von 3",
    headline: "Deine Muster",
    body:
      "Vier Muster erklären, warum Menschen in Konflikten so reagieren wie sie " +
      "reagieren. Deins ist einer davon. Und du siehst, wie du mit den anderen " +
      "drei besser umgehst.",
    highlight: "pionier-was-du-tun-kannst-section",
  },
  {
    id: 3,
    icon: LayoutDashboard,
    accent: "#059669",
    accentBg: "#D1FAE5",
    kicker: "Schritt 3 von 3",
    headline: "Dein PocketCoach",
    body:
      "Deine Erkenntnisse, deine Flexpfade, deine Chronik — alles zum Mitnehmen. " +
      'Du kommst jederzeit über „Mein PocketCoach“ oben rechts wieder hier her.',
    highlight: "tool-session-section",
  },
];

export default function CockpitTour({ forceOpen = false }) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  // Auto-open beim ersten Mount, wenn User Tour noch nicht abgeschlossen hat
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStepIndex(0);
      pushAnalytics("cockpit_tour_started", { force: true });
      return;
    }
    if (!readCompleted()) {
      // Kurzer Delay, damit Cockpit erst rendert und nicht sofort verdeckt wird
      const t = setTimeout(() => {
        setOpen(true);
        setStepIndex(0);
        pushAnalytics("cockpit_tour_started");
      }, 600);
      return () => clearTimeout(t);
    }
  }, [forceOpen]);

  // Body-Scroll blockieren waehrend Tour offen ist
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const closeAndComplete = useCallback(() => {
    writeCompleted();
    setOpen(false);
    pushAnalytics("cockpit_tour_completed");
  }, []);

  const closeAsSkip = useCallback(() => {
    writeCompleted();
    setOpen(false);
    pushAnalytics("cockpit_tour_skipped_at_step", { step: stepIndex + 1 });
  }, [stepIndex]);

  const handleNext = useCallback(() => {
    if (stepIndex >= STEPS.length - 1) {
      closeAndComplete();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [stepIndex, closeAndComplete]);

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Swipe-Handling fuer Mobile: horizontales Swipe left = next, right = back
  const onTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 60) {
      if (dx < 0) handleNext();
      else handleBack();
    }
    setTouchStartX(null);
  };

  if (!open) return null;
  const step = STEPS[stepIndex];
  const Icon = step.icon;
  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="PocketCoach-Einführung"
      className="fixed inset-0 z-[10001]"
      data-testid="cockpit-tour"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-[3px]"
        onClick={closeAsSkip}
        data-testid="cockpit-tour-backdrop"
      />

      {/* Card: Bottom-Sheet auf Mobile, zentriert auf md+ */}
      <div
        className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center md:p-4"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="mx-auto w-full md:max-w-md bg-white rounded-t-3xl md:rounded-2xl shadow-2xl overflow-hidden">
          {/* Header — Skip + Progress */}
          <div className="flex items-center justify-between px-5 pt-4">
            <div className="flex items-center gap-1.5" data-testid="cockpit-tour-progress">
              {STEPS.map((s, i) => (
                <div
                  key={s.id}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIndex
                      ? "w-6 bg-slate-900"
                      : i < stepIndex
                      ? "w-4 bg-slate-500"
                      : "w-4 bg-slate-200"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={closeAsSkip}
              className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
              aria-label="Tour überspringen"
              data-testid="cockpit-tour-skip"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 pt-4 pb-6">
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-4"
              style={{ backgroundColor: step.accentBg, color: step.accent }}
              data-testid="cockpit-tour-kicker"
            >
              <Icon className="w-3.5 h-3.5" />
              {step.kicker}
            </div>
            <h2
              className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3"
              data-testid={`cockpit-tour-headline-${step.id}`}
            >
              {step.headline}
            </h2>
            <p
              className="text-sm sm:text-base text-slate-700 leading-relaxed"
              data-testid={`cockpit-tour-body-${step.id}`}
            >
              {step.body}
            </p>
          </div>

          {/* Footer — Navigation */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={stepIndex === 0}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-2 py-2"
              data-testid="cockpit-tour-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-bold text-white shadow-sm transition-colors"
              style={{ backgroundColor: step.accent }}
              data-testid={isLast ? "cockpit-tour-finish" : "cockpit-tour-next"}
            >
              {isLast ? (
                <>
                  Los geht{"'"}s
                  <CheckCircle2 className="w-4 h-4" />
                </>
              ) : (
                <>
                  Weiter
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
